import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { ApexLogo } from "@/components/ApexLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #142234, #07111E)" }}>
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><ApexLogo light /></div>
        <div className="rounded-2xl border border-navy-light bg-navy-surface p-8 shadow-2xl">
          <h1 className="font-serif text-2xl font-semibold text-white">Choose a new password</h1>
          <p className="mt-1.5 text-sm text-slate-300">Enter a new password for your ApexPath admin account. It must be at least 8 characters.</p>
          {!token ? (
            <p className="mt-4 rounded-lg border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-300">This reset link is invalid or has expired. Please request a new one.</p>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="password" className="text-slate-200">New password</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 pr-10" required minLength={8} data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShow((s) => !s)} data-testid="reset-password-toggle"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-700" aria-label={show ? "Hide password" : "Show password"}>
                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm" className="text-slate-200">Confirm new password</Label>
                <div className="relative mt-1.5">
                  <Input id="confirm" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password"
                    className="bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 pr-10" required minLength={8} data-testid="reset-confirm-input" />
                </div>
                {confirm && password !== confirm && <p className="mt-1 text-xs text-red-400" data-testid="reset-mismatch">Passwords do not match.</p>}
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button type="submit" disabled={loading || (confirm && password !== confirm)} className="btn-gold w-full disabled:opacity-60" data-testid="reset-submit-btn">
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
          <Link to="/admin/login" className="mt-4 block text-center text-xs text-slate-400 hover:text-gold">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
